import {
  AlertCircle,
  Check,
  ChevronRight,
  Loader2,
  Move,
  Pencil,
  PenLine,
  Plus,
  StickyNote,
  Trash2,
  Highlighter,
  X,
} from 'lucide-react';
import { useEffect, useRef } from 'react';
import clsx from 'clsx';
import { PDF_COLOURS, pdfColour, type PdfColour, type PdfPoint } from './studyPdfPalette';
import type { StudyPdfAnnotation, StudyPdfAnnotationInput } from './studyApi';
import { pdfIconButton } from './StudyPdfToolbar';

interface Props {
  items: StudyPdfAnnotation[];
  loading: boolean;
  error: string | null;
  busy: boolean;
  locked: boolean;
  editingId: string | null;
  movingItem: StudyPdfAnnotation | null;
  notePoint: PdfPoint | null;
  noteBody: string;
  unsaved: StudyPdfAnnotationInput | null;
  feedback: string | null;
  feedbackError: boolean;
  hasMore: boolean;
  loadingMore: boolean;
  onAdd: () => void;
  onRetry: () => void;
  onMove: (item: StudyPdfAnnotation) => void;
  onDelete: (item: StudyPdfAnnotation) => void;
  onEdit: (item: StudyPdfAnnotation) => void;
  onColour: (item: StudyPdfAnnotation, colour: PdfColour) => void;
  onBody: (value: string) => void;
  onSave: () => void;
  onCancel: () => void;
  onCancelMove: () => void;
  onRetrySave: () => void;
  onDiscard: () => void;
  onLoadMore: () => void;
}

export function StudyPdfNotesPanel(props: Props) {
  const { items, editingId, notePoint, noteBody, busy, locked, unsaved, movingItem } = props;
  const editor = useRef<HTMLTextAreaElement>(null);
  useEffect(() => {
    if (notePoint || editingId) editor.current?.focus({ preventScroll: true });
  }, [notePoint, editingId]);
  const drafting = Boolean(notePoint || editingId);
  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div
        hidden={drafting}
        className={clsx(
          drafting ? 'hidden' : 'flex',
          'shrink-0 items-center justify-between gap-3 border-b border-hairline px-4 py-3',
        )}
      >
        <div>
          <h3 className="text-sm font-extrabold text-ink">My page notes</h3>
          <p className="mt-0.5 text-xs text-slate2">{items.length} saved on this page</p>
        </div>
        <button
          type="button"
          aria-label="Add page note with keyboard"
          onClick={props.onAdd}
          disabled={locked || props.loading || Boolean(props.error) || drafting}
          className="btn-pill-secondary min-h-11 gap-1.5 px-3 text-xs"
        >
          <Plus className="h-4 w-4" />
          Add note
        </button>
      </div>
      <div className="min-h-0 flex-1 space-y-2 overflow-y-auto p-3">
        {props.loading && (
          <p role="status" className="flex items-center gap-2 p-3 text-sm text-slate2">
            <Loader2 className="h-4 w-4 animate-spin" />
            Loading annotations…
          </p>
        )}
        {props.error && (
          <div role="alert" className="rounded-2xl bg-wash-coral p-3 text-sm text-ink">
            <p>{props.error}</p>
            <button
              type="button"
              onClick={props.onRetry}
              className="btn-pill-secondary mt-2 min-h-11 text-xs"
            >
              Retry annotations
            </button>
          </div>
        )}
        {!props.loading && !props.error && items.length === 0 && !drafting && (
          <div className="rounded-2xl border border-dashed border-hairline bg-canvas p-4">
            <StickyNote className="mb-3 h-6 w-6 text-brand-coral" />
            <p className="text-sm font-bold text-ink">Make this page yours</p>
            <p className="mt-2 text-xs leading-relaxed text-slate2">
              Highlight a key idea, draw a connection, or add a note in your own words.
            </p>
          </div>
        )}
        {items.map((item, index) => {
          const Icon =
            item.kind === 'ink' ? PenLine : item.kind === 'highlight' ? Highlighter : StickyNote;
          const label =
            item.kind === 'ink' ? 'Drawing' : item.kind === 'highlight' ? 'Highlight' : 'Text note';
          return (
            <article
              key={item.id}
              aria-label={label + ' ' + (index + 1)}
              className={clsx(
                'rounded-2xl border bg-canvas-pure p-3',
                movingItem?.id === item.id ? 'border-brand-sky bg-wash-sky' : 'border-hairline',
              )}
            >
              <div className="flex items-center gap-2">
                <span
                  className={clsx(
                    'flex h-8 w-8 shrink-0 items-center justify-center rounded-xl bg-surface',
                    item.kind === 'note'
                      ? 'text-brand-coral'
                      : pdfColour(item.geometry.colour).text,
                  )}
                >
                  <Icon className="h-4 w-4" />
                </span>
                <div className="min-w-0 flex-1">
                  <p className="text-xs font-bold text-ink">{label}</p>
                  <p className="text-[11px] text-slate2">
                    {item.kind === 'note'
                      ? 'Your own words'
                      : pdfColour(item.geometry.colour).label + ' mark'}
                  </p>
                </div>
                <button
                  type="button"
                  aria-label={'Move ' + label.toLowerCase() + ' ' + (index + 1)}
                  title="Move annotation"
                  disabled={locked || drafting}
                  onClick={() => props.onMove(item)}
                  className={pdfIconButton}
                >
                  <Move className="h-4 w-4" />
                </button>
                <button
                  type="button"
                  aria-label={'Delete ' + label.toLowerCase() + ' ' + (index + 1)}
                  title="Delete annotation"
                  disabled={locked || drafting}
                  onClick={() => props.onDelete(item)}
                  className={clsx(pdfIconButton, 'text-danger-600')}
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
              {item.kind === 'note' ? (
                <>
                  <p className="mt-3 whitespace-pre-wrap break-words text-sm leading-relaxed text-ink-soft">
                    {item.body}
                  </p>
                  <button
                    type="button"
                    disabled={locked || drafting}
                    onClick={() => props.onEdit(item)}
                    className="mt-2 inline-flex min-h-11 items-center gap-1.5 rounded-xl px-2 text-xs font-bold text-ink hover:bg-surface"
                  >
                    <Pencil className="h-3.5 w-3.5" />
                    Edit
                  </button>
                </>
              ) : (
                <label className="mt-2 flex items-center justify-between gap-2 border-t border-hairline-soft pt-2 text-xs text-slate2">
                  Colour
                  <select
                    aria-label={'Colour for ' + label.toLowerCase() + ' ' + (index + 1)}
                    value={pdfColour(item.geometry.colour).id}
                    disabled={locked || drafting}
                    onChange={(event) => props.onColour(item, event.target.value as PdfColour)}
                    className="min-h-11 rounded-xl border border-hairline bg-canvas-pure px-2 font-bold text-ink"
                  >
                    {PDF_COLOURS.map((colour) => (
                      <option key={colour.id} value={colour.id}>
                        {colour.label}
                      </option>
                    ))}
                  </select>
                </label>
              )}
            </article>
          );
        })}
        {props.hasMore && (
          <button
            type="button"
            disabled={props.loadingMore || locked}
            onClick={props.onLoadMore}
            className="btn-pill-secondary w-full text-xs"
          >
            {props.loadingMore ? 'Loading…' : 'Load more'}
            <ChevronRight className="h-4 w-4" />
          </button>
        )}
      </div>
      {movingItem && (
        <div className="flex shrink-0 items-center justify-between gap-2 border-t border-hairline bg-wash-sky px-3 py-2">
          <p className="text-xs font-bold text-ink">Click the PDF to place this mark.</p>
          <button
            type="button"
            aria-label="Cancel move"
            onClick={props.onCancelMove}
            className={pdfIconButton}
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      )}
      {drafting && (
        <div className="shrink-0 border-t border-hairline bg-canvas p-3">
          <label htmlFor="study-pdf-note" className="text-xs font-bold text-ink">
            {editingId ? 'Edit note' : 'Note at selected point'}
          </label>
          <textarea
            ref={editor}
            id="study-pdf-note"
            aria-label={editingId ? 'Edit note' : 'Note at selected point'}
            value={noteBody}
            maxLength={2000}
            rows={3}
            disabled={busy || Boolean(unsaved)}
            onChange={(event) => props.onBody(event.target.value)}
            placeholder="Write your thought…"
            className="mt-2 block max-h-32 min-h-20 w-full resize-y rounded-xl border border-hairline bg-canvas-pure p-3 text-sm text-ink focus:border-brand-sky focus:outline-none focus:ring-1 focus:ring-brand-sky"
          />
          <div className="mt-2 flex gap-2">
            <button
              type="button"
              disabled={!noteBody.trim() || busy || Boolean(unsaved)}
              onClick={props.onSave}
              className="btn-pill-primary min-h-11 flex-1 gap-1.5 text-xs"
            >
              <Check className="h-4 w-4" />
              {busy ? 'Saving…' : 'Save note'}
            </button>
            <button
              type="button"
              disabled={busy || Boolean(unsaved)}
              onClick={props.onCancel}
              className="btn-pill-secondary min-h-11 text-xs"
            >
              Cancel
            </button>
          </div>
        </div>
      )}
      {unsaved && (
        <div className="shrink-0 border-t border-hairline bg-wash-coral p-3">
          <p role="alert" className="mb-2 text-xs text-ink">
            {props.feedback ?? 'Your change has not saved.'}
          </p>
          <div className="flex gap-2">
            <button
              type="button"
              disabled={busy}
              onClick={props.onRetrySave}
              className="btn-pill-primary min-h-11 text-xs"
            >
              Retry unsaved change
            </button>
            <button
              type="button"
              disabled={busy}
              onClick={props.onDiscard}
              className="btn-pill-secondary min-h-11 text-xs"
            >
              Discard
            </button>
          </div>
        </div>
      )}
      <p
        role={props.feedbackError && !unsaved ? 'alert' : 'status'}
        className={clsx(
          'flex shrink-0 items-center gap-2 border-t border-hairline px-4 py-3 text-xs text-ink',
          unsaved || props.feedbackError
            ? 'bg-wash-coral'
            : drafting
              ? 'bg-wash-sunshine'
              : 'bg-wash-mint/40',
        )}
      >
        {busy ? (
          <Loader2 className="h-3.5 w-3.5 animate-spin" />
        ) : props.feedbackError ? (
          <AlertCircle className="h-3.5 w-3.5 text-danger-600" />
        ) : (
          <Check className="h-3.5 w-3.5 text-ink" />
        )}
        {busy
          ? 'Saving…'
          : unsaved
            ? 'Not saved · retry above'
            : drafting
              ? 'Draft · not saved yet'
              : (props.feedback ?? 'Changes save as you make them.')}
      </p>
    </div>
  );
}
